// GlobalStateProvider.tsx
import React, { useState, useEffect, createContext, useContext, useCallback } from "react";
import { ethers } from "ethers";
import axios from "axios";
import { apiUrls, getApiUrl } from "../config";
import { useAccount, useDisconnect, useSwitchChain, useWalletClient } from "wagmi";
import { config } from "..";
import { useNavigate, NavigateFunction } from "react-router-dom";
import { useConnectModal } from "@rainbow-me/rainbowkit";

// We import the modal hook so we can open modals (like stake) from within the global state logic
import { useModalState } from "./ModalProvider";
import { clearChatHistory, getLocalChatHistory, migrateLocalChatHistory } from "../views/chatbot/chatHistoryStorage";
import { useTheme } from "next-themes";
import { getToken, setToken, clearToken, migrateTokenFromLocalStorage } from "./tokenManager";

// Migrate any bearer token from localStorage to sessionStorage on module load.
// This ensures users are not logged out after the storage migration is deployed.
migrateTokenFromLocalStorage();

const getEnv = () => {
  // Allow overriding the environment via URL param (dev/staging only, not in production)
  // Use ?env=stage, ?env=development, ?env=production, or ?env=clear to remove
  const isDev = process.env.NODE_ENV === 'development';
  if (isDev) {
    const params = new URLSearchParams(window.location.search);
    const envParam = params.get('env');
    if (envParam === 'clear') {
      localStorage.removeItem('env-override');
    } else if (envParam && ['stage', 'production', 'development'].includes(envParam)) {
      localStorage.setItem('env-override', envParam);
      return envParam;
    }

    const envOverride = localStorage.getItem('env-override');
    if (envOverride && ['stage', 'production', 'development'].includes(envOverride)) {
      return envOverride;
    }
  }

  // Default: detect from hostname
  const host = window.location.host;
  if (host.includes('localhost') || host.includes('127.0.0.1')) return 'stage';
  else if (host.includes('staging')) return 'stage';
  else if (host.includes('coincap.io') || host.includes('production')) return 'production';
  else throw new Error("Unknown environment");
}
export const env = getEnv();

export const STRIPE_CUSTOMER_PORTAL = env === 'production' ? 'https://billing.stripe.com/p/login/3cs2a3bL84xZ2JybII' : 'https://billing.stripe.com/p/login/test_9AQ8zh0NN9VB1R6aEE'

if (process.env.NODE_ENV === 'development') {
  console.log(`%c[CoinCap Pro] env: ${env} | API: ${getApiUrl(env)}`, 'color: #7c3aed; font-weight: bold;')
  if (localStorage.getItem('env-override')) {
    console.log(`%c[CoinCap Pro] Using env override (clear with ?env=clear)`, 'color: #f59e0b;');
  }
}

interface GlobalStateProviderProps {
  children: React.ReactNode;
}

export interface GlobalStateContextType {
  info: any;
  refreshAccountInfo: () => Promise<any | null>;
  stakeMorAndConnectWallet: (amount: number, isUnstake: boolean) => Promise<void>;
  connectAndAssociateWallet: () => Promise<void>;
  transactionDetails: any;
  setTransactionDetails: React.Dispatch<React.SetStateAction<any>>;
  chatHistory: any[];
  setChatHistory: React.Dispatch<React.SetStateAction<any[]>>;
  selectedChatId: number | null;
  setSelectedChatId: React.Dispatch<React.SetStateAction<number | null>>;
  messages: any[];
  setMessages: React.Dispatch<React.SetStateAction<any[]>>;
  registerWallet: () => Promise<void>;
  isSignedIn: () => boolean;
  isSubscriber: () => boolean;
  handleLogout: () => Promise<void>;
  morRewards: any;
  bearerToken: string | null;
  setBearerToken: any;
  selectedChainId: number;
  setSelectedChainId: React.Dispatch<React.SetStateAction<number>>;
  checkNetwork: () => Promise<boolean | undefined>;
  arbitrumRewards: any;
  baseRewards: any;
  rewardContractInfo: any;
  isWithdrawRewards: boolean
  setIsWithdrawRewards: React.Dispatch<React.SetStateAction<boolean>>;
  totalPoints: number
  updatePointsAndRewards: any
}

const GlobalStateContext = createContext<GlobalStateContextType>({} as any);

export const GlobalStateProvider: React.FC<GlobalStateProviderProps> = ({
  children,
}) => {
  const navigate = useNavigate();
  const value = useProvideGlobalState(navigate);

  return (
    <GlobalStateContext.Provider value={value}>
      {children}
    </GlobalStateContext.Provider>
  );
};

export const useGlobalState = () => {
  return useContext(GlobalStateContext);
};


function useProvideGlobalState(navigate: NavigateFunction) {
  const { data: walletClient, isError, isLoading } = useWalletClient();
  const { openConnectModal } = useConnectModal();
  const { disconnect } = useDisconnect();
  const {
    showErrorMessage,
    handleCloseStakeModal,
    showStakeModal,
    showSelectNetworkDialog,
    setStakeModalDetails
  } = useModalState();
  const { switchChain } = useSwitchChain({ config });
  const [info, setInfo] = useState<any>(null);
  const [transactionDetails, setTransactionDetails] = useState<any>(null);
  const [intervalId, setIntervalId] = useState<any>(null);
  const [chatHistory, setChatHistory] = useState<any[]>(getLocalChatHistory());
  const [selectedChatId, setSelectedChatId] = useState<number | null>(null);
  const [messages, setMessages] = useState<any[]>([]);

  const { address, status } = useAccount();
  const [currentAddress, setCurrentAddress] = useState<string | undefined>();

  const [morRewards, setMorRewards] = useState<any>(0);
  const [arbitrumRewards, setArbitrumRewards] = useState<any>(0);
  const [baseRewards, setBaseRewards] = useState<any>(0);
  const [rewardContractInfo, setRewardContractInfo] = useState<any>(0);

  const [bearerToken, setBearerToken] = useState<string | null>(getToken());

  const [selectedChainId, setSelectedChainId] = useState<number>(8453)

  const [isWithdrawRewards, setIsWithdrawRewards] = useState<boolean>(false);

  const [totalPoints, setTotalPoints] = useState<number>(0);

  const checkNetwork = useCallback(async () => {
    if(!walletClient) return
    const provider = new ethers.BrowserProvider(walletClient);
    const network = await provider.getNetwork();
    if(network?.chainId.toString() !== selectedChainId.toString()) {
      const networkName = selectedChainId === 42161 ? 'Arbitrum One' : 'Base'
      showErrorMessage(`Switch your browser wallet to the ${networkName} Network`)
      handleCloseStakeModal();
      return false
    }
    return true
  }, [walletClient, selectedChainId])

  useEffect(() => {
    if (status === 'disconnected' && currentAddress) {
      if (process.env.NODE_ENV === 'development') console.log('disconnected')
      setCurrentAddress(undefined);
     handleLogout();
    } else if (status === 'connected' && address && address !== currentAddress && currentAddress !== undefined) {
      if (process.env.NODE_ENV === 'development') console.log('connected address changed')
      setCurrentAddress(address);

      if(info?.walletAddress && (info?.walletAddress.toLowerCase() !== address.toLowerCase())) {
        if (process.env.NODE_ENV === 'development') console.log('changed to a different address than the current logged in address')
        showErrorMessage(`This wallet is already associated with another account. Log out of your current account and log in again using this wallet to continue.`);
        handleLogout();
      } 
    } else if (status === 'connected' && address && !currentAddress) {
      if (process.env.NODE_ENV === 'development') console.log('connected')
      setCurrentAddress(address);
    }
  }, [status, address, currentAddress]);
  
  useEffect(() => {
    if (bearerToken && info?.email && info?.emailVerified === false) {
      navigate('/confirm_email');
    }
  }, [bearerToken, info?.emailVerified]);

  useEffect(() => {
    if (status === 'connecting') {
      if (process.env.NODE_ENV === 'development') console.log("Wallet is connecting...");
      setCurrentAddress(undefined);
    }
  }, [status]);

  const refreshAccountInfo = useCallback(async (from?: string) => {
    const bearerToken = getToken();
    if (!bearerToken) {
      setInfo(null);
      return null;
    }

    try {
      const response = await axios.post(
        `${getApiUrl(env)}/v3/apiKey/info`,
        {},
        { headers: { Authorization: `Bearer ${bearerToken}` } },
      );
      setInfo(response.data);
      return response.data;
    } catch (err) {
      // clear token on error
      clearToken();
      setBearerToken(null);
      setInfo(null);
      return null;
    }
  }, [env, apiUrls, setInfo]);

  const handleLogout = useCallback(async () => {
    try {
      const bearerToken = getToken();
      await axios.post(
        `${getApiUrl(env)}/v3/apiKey/logout`,
        {},
        { headers: { Authorization: `Bearer ${bearerToken}` } },
      );
    } catch (e) {
      // showErrorMessage("Failed to log out1. Please try again.");
    }
    await refreshAccountInfo();
    clearToken();
    setBearerToken(null);
    clearChatHistory()
    navigate("/signin");
  }, [env, apiUrls, refreshAccountInfo, navigate]);
  
  useEffect(() => {
    // This effect handles wallet setup and refresh, but NOT the interval.
    (async () => {
      try {
        if (isLoading) return;

        if (!walletClient) {
          await refreshAccountInfo();
        } else {
          const token = getToken();
          await axios.post(`${getApiUrl(env)}/v3/apiKey/setbalance`, {
            address: walletClient?.account?.address,
          }, token ? { headers: { Authorization: `Bearer ${token}` } } : undefined);
        }
  
        // Always refresh account info after
        await refreshAccountInfo();
      } catch (err) {
        showErrorMessage("Check your internet connection");
      }
    })();
  
    // No interval logic here.
  }, [walletClient, isLoading, isError]); // keep the deps you actually need
  
  
  useEffect(() => {
    if (intervalId) return;
  
    const iid = setInterval(async () => {
      await refreshAccountInfo("interval");
    }, 10000);
    setIntervalId(iid);
    // Cleanup on unmount
    return () => {
      if (iid) {
        if (process.env.NODE_ENV === 'development') console.log("Clearing interval...");
        clearInterval(iid);
      }
    };
    // We only want this to run once or if we specifically reset intervalId
  }, []);

  /**
   * The actual "associate wallet" logic: sign a message, call backend, refresh info.
   */
  async function associateWallet() {
    const bearerToken = getToken();
    if (!bearerToken) {
      if (process.env.NODE_ENV === 'development') console.log("No bearer token; cannot associate wallet");
      return;
    }
    if(!walletClient) {
      if (process.env.NODE_ENV === 'development') console.log("No wallet client; cannot associate wallet");
      return
    }
    const provider = new ethers.BrowserProvider(walletClient);
    // Make sure we have a provider from the newly connected wallet
    if (!provider) {
      if (process.env.NODE_ENV === 'development') console.log("No wallet provider available; cannot associate wallet");
      return;
    }

    // Prompt the user to sign a message
    const signer = await provider.getSigner();
    const message = `Sign This Message to authenticate with the coincap API. ${Date.now()}`;
    const signedMessage = await signer.signMessage(message);

    const response = await axios.post(
      `${getApiUrl(env)}/v3/apiKey/associate_wallet`,
      { originalMessage: message, signedMessage },
      { headers: { Authorization: `Bearer ${bearerToken}` } },
    );

    if (process.env.NODE_ENV === 'development') console.log("Wallet associated successfully");
    // Refresh info to pick up new walletAddress
    await refreshAccountInfo();
  }

  // Inside useProvideGlobalState (GlobalStateProvider.tsx):
  const [waitingForAssociation, setWaitingForAssociation] = useState(false);

  const [waitingForStakeModal, setWaitingForStakeModal] = useState(false);

  const [isUnstake, setIsUnstake] = useState(false);

  /**
   * Opens the stake modal on the right chain. Arbitrum staking is
   * discontinued, so:
   * - Stake (new deposits): always Base (8453), no chain UI.
   * - Unstake: auto-detect from staked balances. One chain -> switch and open
   *   the stake modal directly. Both chains -> show the SelectNetworkDialog.
   *   Neither -> default Base.
   */
  const proceedToStake = async (amount: number, unstake: boolean) => {
    setStakeModalDetails({ amount, isUnstake: unstake });

    if (!unstake) {
      switchChain({ chainId: 8453 });
      setSelectedChainId(8453);
      showStakeModal();
      return;
    }

    // In the deferred path (connect wallet -> then unstake) `info` may not
    // have refreshed yet, which would make both balances parse to 0 and
    // silently default an Arbitrum-only staker to Base. Refresh first so the
    // auto-detect reads real balances.
    let effectiveInfo = info;
    if (effectiveInfo?.stakedBaseBalance == null && effectiveInfo?.stakedArbitrumBalance == null) {
      try {
        effectiveInfo = (await refreshAccountInfo()) ?? info;
      } catch {
        effectiveInfo = info;
      }
    }

    const stakedBase = parseFloat(effectiveInfo?.stakedBaseBalance ?? '0');
    const stakedArbitrum = parseFloat(effectiveInfo?.stakedArbitrumBalance ?? '0');

    if (stakedBase > 0 && stakedArbitrum > 0) {
      showSelectNetworkDialog(
        [
          { chainId: 8453, name: "Base", amount: stakedBase },
          { chainId: 42161, name: "Arbitrum", amount: stakedArbitrum },
        ],
        "unstake",
      );
      return;
    }

    const chainId = stakedArbitrum > 0 ? 42161 : 8453;
    switchChain({ chainId });
    setSelectedChainId(chainId);
    showStakeModal();
  };

  useEffect(() => {

    async function doit() {
      // If we're waiting to associate AND we now have a connected wallet and are logged in,
      // call associateWallet.
      if (waitingForAssociation && walletClient) {
        await associateWallet().catch((e) => {
          if (process.env.NODE_ENV === 'development') console.log('wallet association error:', e.toString())
          if(e.toString()?.includes('user rejected action')) {
            showErrorMessage(`Error associating wallet: User rejected request`);
          } else {
            showErrorMessage(`This wallet is already associated with another account. Log out of your current account and log in again using this wallet to continue.`);
          }
        })
        setWaitingForAssociation(false);
      }
      if(waitingForStakeModal && walletClient && bearerToken) {
        proceedToStake(1, isUnstake);

        setWaitingForStakeModal(false);
      }
    }

    doit()

  }, [waitingForAssociation, walletClient, waitingForStakeModal, bearerToken]);

  const connectAndAssociateWallet = async () => {
    if (process.env.NODE_ENV === 'development') console.log('connectAndAssociateWallet')
    openConnectModal?.();
    if (!walletClient?.account?.address) {
      // only try to associate if we dont already have a wallet address
      if(!info?.walletAddress) setWaitingForAssociation(true);
    } else {
      // If already connected, just call associate immediately
      try {
        // only try to associate if we dont already have a wallet address
        if(!info?.walletAddress)  await associateWallet()
        if(waitingForStakeModal) {
          proceedToStake(1, isUnstake);
          setWaitingForStakeModal(false);
        }
        setWaitingForAssociation(false);
      } catch(e: any) {
        if (process.env.NODE_ENV === 'development') console.log('wallet association error:', e.toString())
        if(e.toString()?.includes('user rejected action')) {
          showErrorMessage(`Error associating wallet: User rejected request`);
        } else {
          showErrorMessage(`This wallet is already associated with another account. Log out of your current account and log in again using this wallet to continue.`);
        }
        setWaitingForAssociation(false)
      }

    }
  };

  /**
   * The actual "register wallet" logic: sign a message, call backend, store bearerToken, refresh info.
   */
  async function registerWallet() {
    if (process.env.NODE_ENV === 'development') console.log('registerWallet')
    if(!walletClient) {
      if (process.env.NODE_ENV === 'development') console.log("No wallet provider available; cannot register wallet");
      return;
    }
    const provider = new ethers.BrowserProvider(walletClient);

    // Prompt the user to sign a message
    const signer = await provider.getSigner();
    const message = `Sign This Message to register an account with the coincap API. ${Date.now()}`;
    const signedMessage = await signer.signMessage(message);

    // Hit the new endpoint
    const response = await axios.post(
      `${getApiUrl(env)}/v3/apiKey/wallet_register`,
      { originalMessage: message, signedMessage },
    );

    if (process.env.NODE_ENV === 'development') console.log("Wallet registered successfully");

    // The server might return a brand new bearerToken for our new user
    // If so, store that in localStorage
    if (response.data?.bearerToken) {
      setToken(response.data.bearerToken);
      setBearerToken(response.data.bearerToken);
      migrateLocalChatHistory();
    }

    // Optionally, refresh info to pick up this brand new user’s data
    await refreshAccountInfo();
  }

  /**
   * Called when user clicks "Stake MOR OR UNSTAKE MOR" from anywhere in the app
   * ensures they are connected and associated and then shows the stake modal
   */
  const stakeMorAndConnectWallet = async (
    amount: number,
    isUnstake: boolean,
  ) => {
    setIsUnstake(isUnstake);
    if (process.env.NODE_ENV === 'development') console.log('stakeMorAndConnectWallet')
    setWaitingForStakeModal(true);
    const bearerToken = getToken();
    if (!bearerToken) {
      // If not signed in, go to /signin
      navigate("/signin");
      return;
    }

    // If user doesn't have a wallet address yet, connect & associate
    if (!info?.walletAddress || !walletClient?.account?.address) {
      try {
        await connectAndAssociateWallet();
      } catch (e) {
        disconnect();
        showErrorMessage(
          "Current Wallet associated with a different account. You must connect a new wallet with this account before staking.",
        );
        return navigate("/settings");
      }
    } else {
      proceedToStake(amount, isUnstake);
      setWaitingForStakeModal(false);
    }
  };

  const isSignedIn = () => !!(info?.walletAddress || info?.email)
  const isSubscriber = () => (info?.tier ?? 0) > 0 || (info?.stripeTier ?? 0) > 0 || (info?.stakedTier ?? 0) > 0

  const updatePointsAndRewards = useCallback(async () => {
    if(isSignedIn()) {
      try {
        const bearerToken = getToken();
        const response = await axios.post(
          `${getApiUrl(env)}/v3/apiKey/getMorRewards`,
          {},
          { headers: { Authorization: `Bearer ${bearerToken}` } },
        );
        setMorRewards(response?.data?.rewards)
        setArbitrumRewards(response?.data?.arbitrumRewards)
        setBaseRewards(response?.data?.baseRewards)

        if (process.env.NODE_ENV === 'development') {
          console.log('mor rewards response:', response?.data)
          console.log('about to set tp:', response?.data?.totalPoints)
        }
        setTotalPoints(response?.data?.totalPoints)
        setRewardContractInfo({baseRewardContractInfo: response?.data?.baseRewardContractInfo, arbitrumRewardContractInfo: response?.data?.arbitrumRewardContractInfo})
      } catch(e) {
        if (process.env.NODE_ENV === 'development') console.log('Error getting MOR rewards:', e)
      }
    }
  }, [info?.walletAddress, info?.email]);

  useEffect(() => {
    updatePointsAndRewards()
  }, [updatePointsAndRewards]);

  const { setTheme } = useTheme()
  useEffect( () => {
    setTheme('light')
  }, [])

  return {
    info,
    refreshAccountInfo,
    stakeMorAndConnectWallet,
    connectAndAssociateWallet,
    transactionDetails,
    setTransactionDetails,
    chatHistory,
    setChatHistory,
    selectedChatId,
    setSelectedChatId,
    messages,
    setMessages,
    registerWallet,
    isSignedIn,
    isSubscriber,
    handleLogout,
    morRewards,
    bearerToken,
    setBearerToken,
    selectedChainId,
    setSelectedChainId,
    checkNetwork,
    arbitrumRewards,
    baseRewards,
    rewardContractInfo,
    isWithdrawRewards,
    setIsWithdrawRewards,
    totalPoints,
    updatePointsAndRewards
  };
}

export default useProvideGlobalState;
